import {Link} from '@tanstack/react-router';
import {Button} from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {useAnalytics} from '@/hooks/useAnalytics';
import {formatRelativeTime} from '@/utils/displayFormatUtils';
import {getProfileName} from '@/utils/getProfileName';
import {Loader2} from 'lucide-react';
import {FC, useEffect, useRef} from 'react';
import {Typography} from '../ui/typography';
import {EventSection} from '../eventPage/eventSection';
import {ProfileAvatar} from '@/components/profileAvatar/profileAvatar';

export type Participant = {
  id: string;
  registeredAt: Date;
  profile: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  };
};

/** Avatar + name + joined-timestamp row, links to /rider/$profileId. */
function ParticipantRow({participant}: {participant: Participant}) {
  return (
    <Link
      to="/rider/$profileId"
      params={{profileId: participant.profile.id}}
      className="group flex items-center gap-3 p-2 rounded-lg"
    >
      <ProfileAvatar profile={participant.profile} />
      <div className="min-w-0 flex flex-col">
        <Typography
          level="B2"
          className="text-neutral-900 truncate group-hover:underline"
        >
          {getProfileName(participant.profile)}
        </Typography>
        <Typography level="C1" className="text-neutral-500">
          <span className="inline-block">
            Joined {formatRelativeTime(participant.registeredAt)}
          </span>
        </Typography>
      </div>
    </Link>
  );
}

interface ParticipantsCardProps {
  participants: Participant[];
  totalCount: number;
  eventId: string;
  eventName: string;
  allParticipants: Participant[] | null;
  allParticipantsLoading: boolean;
  participantsModalOpen: boolean;
  onOpenParticipants: () => void;
  onCloseParticipants: () => void;
}

/**
 * Shows registered participants as a two-column grid on desktop and a
 * compact avatar row on mobile. The "View All Participants" button on
 * mobile opens a drawer with the full list, each linking to a profile.
 * Hidden when there are no registrations.
 */
export function ParticipantsCard({
  participants,
  totalCount,
  eventId,
  eventName,
  allParticipants,
  allParticipantsLoading,
  participantsModalOpen,
  onOpenParticipants,
  onCloseParticipants,
}: ParticipantsCardProps) {
  const {track} = useAnalytics();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || totalCount === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          track('Participants Card Viewed', {
            event_id: eventId,
            event_name: eventName,
            total_count: totalCount,
          });
          observer.disconnect();
        }
      },
      {threshold: 0.5}
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [track, eventId, eventName, totalCount]);

  if (totalCount === 0) return null;

  return (
    <>
      <div className="flex flex-col" ref={cardRef}>
        {/* Desktop view */}
        <div className="hidden lg:block flex flex-col gap-3">
          <ParticipantTitle
            totalParticipants={participants.length}
            visibleCount={totalCount}
          />
          <div className="grid grid-cols-2 gap-3">
            {participants.map((p) => (
              <ParticipantRow key={p.id} participant={p} />
            ))}
          </div>
        </div>

        {/* Mobile view */}
        <div className="lg:hidden flex flex-col gap-3">
          <ParticipantTitle
            totalParticipants={participants.length}
            visibleCount={totalCount}
          />

          <div className="flex items-center mb-3">
            <div className="flex -space-x-2">
              {participants.slice(0, 5).map((p) => (
                <div key={p.id} className="ring-2 ring-white rounded-full">
                  <ProfileAvatar profile={p.profile} size="sm" />
                </div>
              ))}
            </div>
            {totalCount > 5 && (
              <span className="ml-2 text-sm text-gray-500">
                +{totalCount - 5}
              </span>
            )}
          </div>
          <Button
            variant="outline"
            className="w-full h-10 text-sm font-medium border-gray-300"
            onClick={onOpenParticipants}
          >
            View All Participants
          </Button>
        </div>
      </div>
      <hr />
      <Drawer
        open={participantsModalOpen}
        onOpenChange={(open) => (open ? onOpenParticipants() : onCloseParticipants())}
      >
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{totalCount} Participants</DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-2 pb-6">
            {allParticipantsLoading && allParticipants === null ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
              </div>
            ) : (
              <div className="flex flex-col">
                {(allParticipants ?? participants).map((p) => (
                  <ParticipantRow key={p.id} participant={p} />
                ))}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

interface ParticipantTitleProps {
  /* The number of visible participants */
  visibleCount: number;
  /* The total number of participants */
  totalParticipants: number;
}
const ParticipantTitle: FC<ParticipantTitleProps> = ({
  visibleCount,
  totalParticipants,
}) => (
  <EventSection
    label="Who's Going"
    adornment={
      <span className="bg-primary-400 rounded-full px-2 py-0.5 text-white text-xs font-normal">
        {totalParticipants}
      </span>
    }
  >
    {visibleCount > totalParticipants && (
      <button className="text-sm font-medium text-blue-600 hover:text-blue-700">
        View All
      </button>
    )}
  </EventSection>
);
